import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import {
  CommerceCatalogWorkspace,
  DescriptionText,
  HorizontalStack,
  PropertyGrid,
  PropertyItem,
  Surface,
  VerticalStack,
  toast,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  Checkbox,
  RegistryStatus,
  type CommerceSelectOptionViewModel,
  type ColumnDef,
  type ColumnFiltersState,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';
import { useScope } from '@workspace/scope-context';
import { useNavigation } from '@/context/NavigationContext';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const COMMERCE_ENTITLEMENT_SORT_FIELDS = ['commission', 'created_at', 'name', 'status', 'targets', 'updated_at'] as const;
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  CommerceBreadcrumbHeader,
} from '@/views/commerce/catalogHierarchyShared';
import {
  createAdminResellerEntitlement,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  listAdminResellerEntitlements,
  listCommissionPlans,
  listPublications,
  updateAdminResellerEntitlement,
  type AdminResellerEntitlement,
  type AdminResellerEntitlementInput,
  type CatalogPublication,
  type CommissionPlan,
} from '@/lib/commerce';
import { listAccountTenants, type AccountTenantRecord } from '@/lib/accounts';
import { listLinkedResellers, type PartnerGrantRecord } from '@/lib/partner-links';
import {
  buildPublicationSelectorOptions,
  buildTenantSelectorOptions,
} from '@/views/commerce/selectorOptions';
import { EntitlementWizardEditor, type EntitlementFormState } from './EntitlementWizardEditor';

const noCommissionPlanValue = '__none__';

function emptyForm(publicationId = ''): EntitlementFormState {
  return {
    publicationId,
    commissionPlanId: '',
    name: '',
    description: '',
    status: 'active',
    organizationIds: [],
    sellingTenantIds: [],
    storeIds: [],
    agentUserIds: [],
  };
}

function formFromEntitlement(item: AdminResellerEntitlement): EntitlementFormState {
  return {
    publicationId: item.publication_id,
    commissionPlanId: item.default_commission_plan_id || '',
    name: item.name,
    description: item.description || '',
    status: item.status,
    organizationIds: item.organization_ids || [],
    sellingTenantIds: item.selling_tenant_ids || [],
    storeIds: item.store_ids || [],
    agentUserIds: item.agent_user_ids || [],
  };
}

function collectObservedOptions(
  entitlements: AdminResellerEntitlement[],
  pick: (item: AdminResellerEntitlement) => string[],
  groupLabel: string,
  helperText: string,
): CommerceSelectOptionViewModel[] {
  const options = new Map<string, CommerceSelectOptionViewModel>();
  entitlements.forEach(item => {
    pick(item).forEach(value => {
      const normalized = value.trim();
      if (!normalized || options.has(normalized)) {
        return;
      }
      options.set(normalized, {
        value: normalized,
        label: normalized,
        description: item.name,
        helperText,
        groupLabel,
        keywords: [item.publication_id, item.catalog_id, item.publication_scope].filter(Boolean),
      });
    });
  });
  return Array.from(options.values());
}

function publicationOptionFallback(item: AdminResellerEntitlement): CommerceSelectOptionViewModel {
  return {
    value: item.publication_id,
    label: item.storefront_key?.trim() || item.catalog_key?.trim() || item.publication_id,
    description: item.publication_scope || 'Saved publication',
    helperText: item.publication_status || null,
    groupLabel: 'Saved',
    keywords: [
      item.publication_id,
      item.catalog_id,
      item.catalog_key || '',
      item.storefront_key || '',
      item.publication_scope,
      item.publication_status,
    ].filter(Boolean),
  };
}

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return 'success';
    case 'draft':
      return 'warning';
    case 'archived':
    case 'inactive':
      return 'muted';
    default:
      return 'default';
  }
}

export default function CommerceEntitlementsPage() {
  const { activeTenant, organizations } = useScope();
  const navigation = useNavigation();
  const [publications, setPublications] = useState<CatalogPublication[]>([]);
  const [commissionPlans, setCommissionPlans] = useState<CommissionPlan[]>([]);
  const [entitlements, setEntitlements] = useState<AdminResellerEntitlement[]>([]);
  const [accountTenants, setAccountTenants] = useState<AccountTenantRecord[]>([]);
  const [linkedResellers, setLinkedResellers] = useState<PartnerGrantRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const query = useCommerceRegistryQuery({
    facetKeys: ['status'],
    facetValues: { status: ['active', 'archived', 'draft'] },
    sortFields: COMMERCE_ENTITLEMENT_SORT_FIELDS,
  });
  const searchQuery = query.search;
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);
  const [totalEntitlements, setTotalEntitlements] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const requestSequence = useRef(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AdminResellerEntitlement | null>(null);
  const [form, setForm] = useState<EntitlementFormState>(emptyForm());
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const columnFilters = useMemo<ColumnFiltersState>(() => (
    query.facets.status.length > 0 ? [{ id: 'status', value: query.facets.status }] : []
  ), [query.facets.status]);

  const loadData = async () => {
    const requestId = ++requestSequence.current;
    if (!activeTenant?.id) {
      return;
    }
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    try {
      const [nextPublications, nextCommissionPlans, nextEntitlements, nextLinkedResellers] = await Promise.all([
        listPublications({ catalogOwnerTenantId: activeTenant.id, limit: 200 }).then(page => page.items),
        listCommissionPlans({ catalogOwnerTenantId: activeTenant.id, limit: 200 }).then(page => page.items),
        listAdminResellerEntitlements({
          search: searchQuery || undefined,
          status: query.facets.status,
          sort: query.sort,
          include_facets: ['status'],
          ...toLimitOffset(query.pagination),
        }),
        listLinkedResellers(activeTenant.id, { limit: 200 }).then(page => page.items),
      ]);
      const nextAccountTenants =
        activeTenant.accountId ? await listAccountTenants(activeTenant.accountId) : [];
      if (requestId !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(nextEntitlements.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setPublications(nextPublications || []);
      setCommissionPlans(nextCommissionPlans || []);
      setEntitlements(nextEntitlements.items);
      setTotalEntitlements(nextEntitlements.total_count);
      setStatusCounts(Object.fromEntries((nextEntitlements.facets.status ?? []).map(bucket => [bucket.value, bucket.count])));
      setAccountTenants(nextAccountTenants || []);
      setLinkedResellers(nextLinkedResellers || []);
    } catch (err) {
      if (requestId !== requestSequence.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load entitlement workspace.'));
      }
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeTenant?.id) {
      setPublications([]);
      setCommissionPlans([]);
      setEntitlements([]);
      setTotalEntitlements(0);
      setStatusCounts({});
      setAccountTenants([]);
      setLinkedResellers([]);
      setEditorOpen(false);
      setEditingItem(null);
      setForm(emptyForm());
      setLoading(false);
      setError(null);
      setAccessDeniedStatus(null);
      return;
    }
    void loadData();
  }, [activeTenant?.id, query.facets.status, query.pagination, query.sort, searchQuery]);

  const publicationOptions = useMemo(
    () => {
      const options = buildPublicationSelectorOptions(publications);
      if (!editingItem?.publication_id || options.some(option => option.value === editingItem.publication_id)) {
        return options;
      }
      return [...options, publicationOptionFallback(editingItem)];
    },
    [editingItem, publications],
  );
  const commissionPlanOptions = useMemo(
    () => [
      {
        value: noCommissionPlanValue,
        label: 'No Commission Plan',
        description: 'Leave commission unset until a more specific commission assignment applies.',
      },
      ...commissionPlans.map(plan => ({
        value: plan.commission_plan_id,
        label: plan.name,
        description: plan.status,
        helperText: plan.selling_tenant_id || null,
        keywords: [plan.commission_plan_id, plan.name, plan.status, plan.selling_tenant_id || ''].filter(Boolean),
      })),
    ],
    [commissionPlans],
  );
  const tenantOptions = useMemo(
    () =>
      buildTenantSelectorOptions({
        activeTenant,
        organizations,
        observedTenantIds: entitlements.flatMap(item => item.selling_tenant_ids || []),
        directoryTenants: [
          ...linkedResellers.map(item => ({
            id: item.receivingTenantId,
            name: item.receivingTenantName || item.receivingTenantSlug || item.receivingTenantId,
            slug: item.receivingTenantSlug || null,
            status: item.status,
            accountId: null,
            organizationId: null,
          })),
          ...accountTenants,
        ],
      }),
    [accountTenants, activeTenant, entitlements, linkedResellers, organizations],
  );
  const organizationOptions = useMemo(
    () => collectObservedOptions(entitlements, item => item.organization_ids || [], 'Observed', 'Seen on existing entitlements'),
    [entitlements],
  );
  const storeOptions = useMemo(
    () => collectObservedOptions(entitlements, item => item.store_ids || [], 'Observed', 'Seen on existing entitlements'),
    [entitlements],
  );
  const agentOptions = useMemo(
    () => collectObservedOptions(entitlements, item => item.agent_user_ids || [], 'Observed', 'Seen on existing entitlements'),
    [entitlements],
  );

  const handleEdit = useCallback((item: AdminResellerEntitlement) => {
    setEditingItem(item);
    setForm(formFromEntitlement(item));
    setEditorOpen(true);
  }, []);

  const columns = useMemo<ColumnDef<AdminResellerEntitlement>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'name',
      accessorKey: 'name',
      size: 250,
      header: 'Entitlement',
      cell: ({ row }) => {
        const item = row.original;
        return (
          <button
            type="button"
            onClick={() => handleEdit(item)}
            className="group min-w-0 text-left"
            aria-label={`Edit ${item.name}`}
          >
            <p className="text-sm font-black text-foreground transition-colors group-hover:text-primary">
              {item.name}
            </p>
            <DescriptionText className="line-clamp-1 text-[11px]">
              Publication {item.storefront_key || item.catalog_key || item.publication_id}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      accessorKey: 'status',
      size: 100,
      header: 'Status',
      cell: ({ row }) => (
        <RegistryStatus label={row.original.status} variant={statusVariant(row.original.status)} />
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: 'commission',
      accessorKey: 'default_commission_plan_id',
      size: 180,
      header: 'Commission Plan',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.default_commission_plan_id || 'Not set'}</span>
      ),
    },
    {
      id: 'targets',
      accessorFn: item => item.grants.length,
      size: 100,
      header: 'Targets',
      cell: ({ row }) => (
        <span className="text-[11px] font-black text-primary">{row.original.grants.length}</span>
      ),
    },
    {
      accessorKey: 'updated_at',
      size: 120,
      header: 'Updated',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {new Date(row.original.updated_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 80,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <HorizontalStack gap={2} className="justify-end shrink-0">
            <button
              type="button"
              onClick={() => handleEdit(item)}
              aria-label={`Modify ${item.name}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            >
              <Pencil className="h-3 w-3" />
            </button>
          </HorizontalStack>
        );
      },
    },
  ], [handleEdit]);
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: COMMERCE_ENTITLEMENT_SORT_FIELDS, registry: 'commerce-entitlements' });

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    activeSectionId: 'reseller_entitlements',
    searchQuery,
    searchPlaceholder: 'Search entitlements...',
    onSearchChange: query.setSearch,
    error,
    blockingReason: 'Your current role does not have permission to manage commerce entitlements.',
    onSelectSection: sectionId => navigation.navigate(buildCommerceSectionRoute(sectionId)),
    primaryAction: {
      id: 'new-entitlement',
      label: 'New Entitlement',
      onAction: () => {
        setEditingItem(null);
        setForm(emptyForm(publications[0]?.publication_id || ''));
        setEditorOpen(true);
      },
      disabled: loading || !activeTenant?.id,
    },
  });

  const saveEntitlement = async () => {
    if (!activeTenant?.id) {
      return;
    }
    setSaving(true);
    try {
      const payload: AdminResellerEntitlementInput = {
        catalog_owner_tenant_id: activeTenant.id,
        publication_id: form.publicationId,
        default_commission_plan_id: form.commissionPlanId || '',
        name: form.name.trim(),
        description: form.description.trim() || null,
        status: form.status,
        metadata: {},
        organization_ids: form.organizationIds,
        selling_tenant_ids: form.sellingTenantIds,
        store_ids: form.storeIds,
        agent_user_ids: form.agentUserIds,
      };
      if (editingItem) {
        await updateAdminResellerEntitlement(editingItem.entitlement_id, {
          publication_id: payload.publication_id,
          default_commission_plan_id: payload.default_commission_plan_id,
          name: payload.name,
          description: payload.description,
          status: payload.status,
          metadata: payload.metadata,
          organization_ids: payload.organization_ids,
          selling_tenant_ids: payload.selling_tenant_ids,
          store_ids: payload.store_ids,
          agent_user_ids: payload.agent_user_ids,
        });
        toast.success('Entitlement updated');
      } else {
        await createAdminResellerEntitlement(payload);
        toast.success('Entitlement created');
      }
      await loadData();
      setEditorOpen(false);
      setEditingItem(null);
      setForm(emptyForm(publications[0]?.publication_id || ''));
    } catch (err) {
      const message = getCommerceErrorMessage(err, 'Failed to save entitlement.');
      setError(message);
      toast.error('Failed to save entitlement', { description: message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      padded={false}
      titleContent={
        <CommerceBreadcrumbHeader
          breadcrumbs={[{ label: 'Entitlements' }]}
          title="Entitlements"
          description="Decide who can resell each publication without editing publication release settings."
        />
      }
      takeoverActive={editorOpen}
      replaceShellHeaderWhenTakeoverActive
      takeoverContent={
        <EntitlementWizardEditor
          isOpen={editorOpen}
          editingItem={editingItem}
          form={form}
          setForm={setForm}
          onSave={saveEntitlement}
          onClose={() => {
            setEditorOpen(false);
            setEditingItem(null);
          }}
          saving={saving}
          publicationOptions={publicationOptions}
          commissionPlanOptions={commissionPlanOptions}
          tenantOptions={tenantOptions}
          organizationOptions={organizationOptions}
          storeOptions={storeOptions}
          agentOptions={agentOptions}
        />
      }
    >
      <RegistryWorkspace>
        <RegistryTableSurface>
          <DataTable
            mode="manual"
            columns={columns}
            data={entitlements}
            sorting={sorting}
            onSortingChange={updater => {
              const next = typeof updater === 'function' ? updater(sorting) : updater;
              query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
            }}
            globalFilter={searchQuery}
            columnFilters={columnFilters}
            onColumnFiltersChange={updater => {
              const next = typeof updater === 'function' ? updater(columnFilters) : updater;
              const filter = next.find(item => item.id === 'status');
              query.setFacet('status', Array.isArray(filter?.value) ? filter.value.map(String) : []);
            }}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            pagination={query.pagination}
            onPaginationChange={query.onPaginationChange}
            manualPagination
            totalRowCount={totalEntitlements}
            loading={loading}
            className="border-none rounded-none"
            paginationClassName="px-0 py-0"
            showPagination
            emptyState={
              <div className="px-0 py-12 text-center">
                <p className="text-sm font-black text-foreground">No Entitlements</p>
                <DescriptionText className="mt-1 text-xs">
                  {searchQuery || query.facets.status.length > 0
                    ? 'No entitlements matched the current search or filters.'
                    : 'Create the first entitlement to grant reseller access to a publication.'}
                </DescriptionText>
                {searchQuery || query.facets.status.length > 0 ? (
                  <button type="button" onClick={query.clear} className="mt-3 text-xs font-semibold text-primary hover:underline">
                    Reset search and filters
                  </button>
                ) : null}
              </div>
            }
            renderToolbar={table => (
              <DataTableToolbar
                table={table}
                searchValue={searchQuery}
                onSearchChange={query.setSearch}
                searchPlaceholder="Search all entitlements..."
                resultCount={totalEntitlements}
                className="border-b border-border/40 bg-transparent px-0 py-0"
                filters={
                  <DataTableFacetedFilter
                    title="Status"
                    selectedValues={new Set(query.facets.status)}
                    onFilterChange={values => query.setFacet('status', Array.from(values))}
                    counts={statusCounts}
                    options={[
                      { label: 'Active', value: 'active' },
                      { label: 'Draft', value: 'draft' },
                      { label: 'Archived', value: 'archived' },
                    ]}
                  />
                }
              />
            )}
          />
        </RegistryTableSurface>
      </RegistryWorkspace>
    </CommerceCatalogWorkspace>
  );
}
